import { useState } from "react";
import { VORDERHAUS_GALLERY, HINTERHAUS_GALLERY } from "@/data/apartments";
import { Lightbox } from "./Lightbox";
import { ZoomIn } from "lucide-react";
import { useT } from "@/i18n/LanguageProvider";

export const ExteriorGallery = () => {
    const t = useT();
    const [lightbox, setLightbox] = useState({ open: false, images: [], index: 0 });

    const open = (images, index) => setLightbox({ open: true, images, index });
    const close = () => setLightbox((s) => ({ ...s, open: false }));

    const vorderhausImages = VORDERHAUS_GALLERY.map((url, i) => ({
        url,
        caption: `${t({ de: "Vorderhaus · Ansicht", en: "Front building · view" })} ${i + 1}`,
    }));
    const hinterhausImages = HINTERHAUS_GALLERY.map((url, i) => ({
        url,
        caption: `${t({ de: "Hinterhaus · Loft-Haus mit Carports · Ansicht", en: "Rear · loft house with carports · view" })} ${i + 1}`,
    }));

    return (
        <section className="bg-cream py-24 sm:py-32" data-testid="exterior-gallery">
            <div className="container-page">
                <div className="grid lg:grid-cols-12 gap-12 mb-12">
                    <div className="lg:col-span-6">
                        <span className="overline">{t({ de: "04 / 12 — Architektur", en: "04 / 12 — Architecture" })}</span>
                        <h2 className="h-section mt-4">
                            {t({ de: "Klassische Dachform,", en: "Classic roof shape," })}
                            <span className="italic text-brand-500"> {t({ de: "moderne Linien.", en: "modern lines." })}</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-5 lg:col-start-8">
                        <p className="body-md">
                            {t({
                                de: <>Aufwändig gestaltete Architektur mit Satteldach inkl. integriertem Flachbereich – für klassische Silhouette und großzügige Dachterrassen. Hochwertige Fassadengestaltung, Dreifachverglasung, moderne Gebäudetechnik. <em className="text-stone-500">Bilder klicken zum Vergrößern.</em></>,
                                en: <>Carefully designed architecture with a gable roof including an integrated flat section – for a classic silhouette and generous roof terraces. High-quality façade, triple glazing, modern building services. <em className="text-stone-500">Click images to zoom.</em></>,
                            })}
                        </p>
                    </div>
                </div>

                <div>
                    <h3 className="font-heading text-2xl text-ink mb-5" data-testid="gallery-vorderhaus-title">
                        {t({ de: "Vorderhaus · 3-Familienhaus", en: "Front building · 3-family home" })}
                    </h3>
                    <div className="grid grid-cols-12 gap-4 lg:gap-6">
                        <ZoomTile className="col-span-12 lg:col-span-8 aspect-[16/10]" src={vorderhausImages[0].url} alt="Vorderhaus Hauptansicht" onClick={() => open(vorderhausImages, 0)} />
                        <ZoomTile className="col-span-6 lg:col-span-4 aspect-[16/10] lg:aspect-auto" src={vorderhausImages[1].url} alt="Vorderhaus seitlich" onClick={() => open(vorderhausImages, 1)} />
                        <ZoomTile className="col-span-6 lg:col-span-4 aspect-[4/3]" src={vorderhausImages[2].url} alt="Vorderhaus Detail" onClick={() => open(vorderhausImages, 2)} />
                        <ZoomTile className="col-span-12 lg:col-span-8 aspect-[16/9]" src={vorderhausImages[3].url} alt="Vorderhaus Straßenansicht" onClick={() => open(vorderhausImages, 3)} />
                    </div>
                </div>

                <div className="mt-20">
                    <h3 className="font-heading text-2xl text-ink mb-5" data-testid="gallery-hinterhaus-title">
                        {t({ de: "Hinterhaus · Loft-Haus mit Carports", en: "Rear · loft house with carports" })}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {hinterhausImages.map((it, i) => (
                            <ZoomTile
                                key={i}
                                className="aspect-[4/3]"
                                src={it.url}
                                alt={it.caption}
                                onClick={() => open(hinterhausImages, i)}
                            />
                        ))}
                    </div>
                    <p className="font-body text-sm text-stone-500 mt-5 max-w-2xl">
                        {t({
                            de: "Im Erdgeschoss des Hinterhauses befinden sich die PKW-Carports – einer pro Wohnung – inklusive Wallbox-Vorbereitung. Darüber liegt die Loft-Maisonette des Projekts.",
                            en: "On the ground floor of the rear building you'll find the carports – one per apartment – with wallbox preparation. Above sits the project's loft maisonette.",
                        })}
                    </p>
                </div>
            </div>

            <Lightbox
                open={lightbox.open}
                images={lightbox.images}
                index={lightbox.index}
                onClose={close}
            />
        </section>
    );
};

const ZoomTile = ({ src, alt, onClick, className = "" }) => (
    <button
        type="button"
        onClick={onClick}
        className={`group frame-img relative cursor-zoom-in bg-stone-200 overflow-hidden ${className}`}
        data-testid="exterior-zoom-tile"
    >
        <img src={src} alt={alt} className="w-full h-full object-cover" />
        <span className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/15 transition-colors" />
        <span className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <ZoomIn size={16} className="text-ink" />
        </span>
    </button>
);
